"use client";

import { useRef, useState, useEffect, type FC, type Ref } from "react";
import {
  Container,
  type ContainerProps,
  type GalleryProps,
  Heading,
  type ClassNameProp,
  mergeRefs,
  GalleryCarousel,
  Icon,
  Flex,
  Text,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { mergeClasses, padNumber } from "@huub-web/utils";

import { Section } from "../Section";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import { AnalysisIndicator } from "../AnalysisIndicator";
import { SummaryLoading } from "../Analysis/components/SummaryLoading";
import {
  carouselContainerStyle,
  messageStyle,
  carouselStyle,
} from "./AnalysisGallery.css";
import {
  analysisSectionIllustrations,
  type AnalysisSectionIndex,
} from "../Analysis/types/AnalysisSections";

export type AnalysisGalleryProps = ClassNameProp & {
  /**
   * Index of the gallery
   **/
  index?: AnalysisSectionIndex;
  /**
   * Array of image URLs to display in the gallery
   **/
  images: GalleryProps["images"];
  /**
   * Title for the gallery section
   **/
  title?: string;
  /**
   * Description text for the gallery section
   **/
  description?: string;
  /**
   * Container size
   **/
  container?: ContainerProps["size"];
  /**
   * Loading state
   **/
  loading?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * AnalysisGallery component displays a gallery of images
 * with an optional heading and description
 */
export const AnalysisGallery: FC<AnalysisGalleryProps> = ({
  index,
  images,
  title = "Een kijkje binnen",
  description,
  container = "marketing",
  loading = false,
  className,
  ref,
}) => {
  const internalRef = useContentMenuSection<HTMLDivElement>({
    id: "gallery",
    label: title,
    index,
  });

  const { t } = useTranslation();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [carouselHeight, setCarouselHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => {
      if (carouselRef.current) {
        const height = carouselRef.current.scrollHeight;
        setCarouselHeight(height);
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [images, loading]);

  return (
    <Section
      id={`analysisSection${index}`}
      ref={mergeRefs(ref, internalRef)}
      className={mergeClasses(className)}
    >
      <Container size={container}>
        {index !== undefined && (
          <AnalysisIndicator
            index={index}
            illustration={analysisSectionIllustrations[index]}
          />
        )}
        <Heading
          title={title}
          description={
            loading ||
            (loading === false && (!images || images.length === 0)) ? (
              <SummaryLoading />
            ) : (
              description
            )
          }
          variant="main"
          align="center"
        />
      </Container>

      {((images && images.length > 0) || loading) && (
        <div
          className={carouselContainerStyle}
          style={{
            height: carouselHeight > 0 ? `${carouselHeight}px` : "auto",
          }}
        >
          <div className={carouselStyle} ref={carouselRef}>
            <GalleryCarousel
              items={images}
              variant="carousel"
              container={container}
              navigation="arrows"
              loading={loading}
              default={3}
              fadeOutInactive
              loadingChildren={
                <Flex
                  className={messageStyle}
                  gap="xs"
                  align="center"
                  justify="center"
                >
                  <Icon icon="loading" animation="rotation" size="s" />
                  <Text>{t("analysis.loading")}</Text>
                </Flex>
              }
              carouselClassName="skip-overflow"
            />
          </div>
        </div>
      )}

      {index !== undefined && (
        <Text align="center">
          {padNumber(index)} &ndash; {title}
        </Text>
      )}
    </Section>
  );
};
